import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BaseService } from '@core/services/base.service';
import { readListFromResponse, readObjectFromResponse } from '@core/helpers/api-response.helper';
import { filenameFromContentDisposition } from '@core/helpers/download-file.helper';
import { generateSecureIdempotencyKey } from '@core/helpers/idempotency-key.helper';
import { CARTS } from '@core/constant/api.constants';
import { NO_CART_MODE_MESSAGE } from '../models/saved-cart.models';
import type {
  CartActionResponse,
  CartCloneResult,
  CartDraftRow,
  CartPortalUser,
  CartDraftStamp,
  CartDraftTimeZone,
  CartMode,
  CartShareAction,
  SavedCartType,
  SwitchCartTypeRequest,
  CartMarkSignedResponse,
  CartCopyLinkResponse,
  CartImpersonateResponse,
  CartInviteUserResponse,
  SavedCartDetail,
  SavedCartFilterOptionsData,
  SavedCartFilterOptionsResponse,
  SavedCartListQuery,
  SavedCartListResponse,
  SavedCartStage,
  StartCartFromHubspotDto,
  StartCartFromHubspotResult,
  UpdateCartFeesDto,
  UpdateCartItemPayload,
} from '../models/saved-cart.models';

type AgreementFormat = 'pdf' | 'docx';

const AGREEMENT_MIME: Record<AgreementFormat, string> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

@Injectable({ providedIn: 'root' })
export class SavedCartService {
  private readonly baseService = inject(BaseService);

  async getCarts(query: SavedCartListQuery): Promise<SavedCartListResponse> {
    let params = new HttpParams().set('page', query.page).set('limit', query.limit);

    if (query.stage) params = params.set('stage', query.stage);

    const search = query.search?.trim();
    if (search) params = params.set('search', search);

    if (query.from_date && query.to_date) {
      params = params.set('from_date', query.from_date).set('to_date', query.to_date);
    }

    for (const cityId of query.city_ids ?? []) {
      params = params.append('city_ids', cityId);
    }

    for (const year of query.show_years ?? []) {
      params = params.append('show_years', year);
    }

    for (const status of query.statuses ?? []) {
      params = params.append('statuses', status);
    }

    return await firstValueFrom(
      this.baseService.http.get<SavedCartListResponse>(CARTS.GET_LIST, { params }),
    );
  }

  async getFilterOptions(stage?: SavedCartStage): Promise<SavedCartFilterOptionsData> {
    const response = (await this.baseService.callGetApi(
      CARTS.GET_FILTER_OPTIONS,
      stage ? { stage } : {},
    )) as SavedCartFilterOptionsResponse;
    const data = readObjectFromResponse<SavedCartFilterOptionsData>(
      response,
      'cities',
      'show_years',
      'statuses',
    );
    return {
      cities: data?.cities ?? [],
      show_years: data?.show_years ?? [],
      statuses: data?.statuses ?? [],
    };
  }

  /**
   * The modes read, shared by every caller for as long as the app is open.
   *
   * Holding the promise rather than the result also collapses concurrent callers into one request,
   * which is the case that matters: the builder asks for the modes twice on the way in — once for
   * the header dropdown and once for the cart it claims — and those two overlap.
   */
  private modesRequest: Promise<CartMode[]> | null = null;

  /**
   * The cart modes the logged-in user may build in (US 102.1), in dropdown order.
   *
   * An empty array is a real answer, not a failure: the user holds no mode role, and since
   * `role_id` is required to create a cart, they cannot create one at all.
   *
   * Cached because this is per-user reference data that cannot change while the app is open — a
   * role assignment takes effect on the next sign-in — and six call sites across the builder, the
   * listings and the bootstrap pages ask for it. Repeating the request on every one of them is
   * what pushed the builder into the API's per-IP rate limit on reload.
   */
  async listModes(): Promise<CartMode[]> {
    this.modesRequest ??= this.fetchModes().catch((err: unknown) => {
      // A failure is never cached: a blip on the way in must not leave the dropdown empty, and
      // every caller unable to create a cart, for the rest of the session.
      this.modesRequest = null;
      throw err;
    });
    return this.modesRequest;
  }

  private async fetchModes(): Promise<CartMode[]> {
    const response = await this.baseService.callGetApi(CARTS.CONTRACT_TYPE_MODES);
    return readListFromResponse<CartMode>(response);
  }

  /**
   * The mode to create a cart in when the screen offers no choice of one.
   *
   * The modes come back in dropdown order — Sales first — so the first is the sensible default for
   * every entry point that is not the builder's own department picker. Throws when the user holds
   * none, because there is no cart to create in that case and the caller must say so rather than
   * send a request the API will reject.
   */
  async resolveDefaultRoleId(): Promise<string> {
    const [mode] = await this.listModes();
    if (!mode?.role_id) throw new Error(NO_CART_MODE_MESSAGE);
    return mode.role_id;
  }

  /**
   * Create an empty cart for the builder to fill in. `stage` is the flow type — `contract` or
   * `proposal`; everything else is PATCHed in later.
   *
   * `roleId` is the cart mode from {@link listModes} (US 102.1) and is required: the cart is fixed
   * to that mode until {@link switchContractType} moves it. `cartType` opens the cart straight
   * into the Add-On or Onsite flow; omitted, the server defaults it to a contract.
   *
   * Goes through `http` directly rather than {@link BaseService}, which carries no custom headers:
   * the API requires an `Idempotency-Key` so a retried creation returns the original cart instead
   * of a duplicate. The key is generated per call, so callers must not retry on their own — one
   * click, one key, one cart.
   */
  async createCart(
    stage: SavedCartStage,
    roleId: string,
    cartType?: SavedCartType,
  ): Promise<SavedCartDetail> {
    const response = await firstValueFrom(
      this.baseService.http.post(
        CARTS.CREATE,
        { stage, role_id: roleId, ...(cartType ? { cart_type: cartType } : {}) },
        { headers: { 'Idempotency-Key': generateSecureIdempotencyKey() } },
      ),
    );
    const data = readObjectFromResponse<SavedCartDetail>(response, 'id', 'stage');
    if (!data?.id) throw new Error('Cart could not be created');
    return data;
  }

  /**
   * Moves a cart between the Contract, Add-On and Onsite flows, or to another mode (US 102.1).
   *
   * `confirm` is the server's safety net for a switch that destroys something — every move into or
   * out of `add_on` — and is also named for a shared cart going Onsite → Contract. It rides as a
   * query param, and asking for the type the cart already has is a no-op. A mode change always
   * clears the cart and takes no `confirm`: the dialog is the caller's.
   *
   * Answers with the recomputed cart, so callers should take its `cart_type` as the truth rather
   * than the one they asked for.
   */
  async switchContractType(
    cartId: number | string,
    { cartType, roleId, confirm }: SwitchCartTypeRequest,
  ): Promise<SavedCartDetail> {
    const response = await this.baseService.callPatchApi(
      confirm
        ? `${CARTS.SWITCH_CONTRACT_TYPE(cartId)}?confirm=true`
        : CARTS.SWITCH_CONTRACT_TYPE(cartId),
      // Each half is sent only when it is the one being moved: the endpoint takes either, and
      // restating the one the cart already has would be a no-op at best and a needless reset of
      // the other at worst.
      {
        ...(cartType ? { cart_type: cartType } : {}),
        ...(roleId ? { role_id: roleId } : {}),
      },
    );
    const data = readObjectFromResponse<SavedCartDetail>(response, 'id', 'stage');
    if (!data?.id) throw new Error('The cart type could not be changed');
    return data;
  }

  /**
   * The HubSpot entry point: claims a cart, puts the linked contact on it and binds a deal —
   * everything the loader page used to assemble from three separate calls.
   *
   * Safe to call twice. An untouched draft for the same HubSpot record and flow comes back with
   * `reused: true` rather than a second cart being minted, which is what makes a refresh, a
   * double-click or a browser Back harmless. The `Idempotency-Key` covers the narrower race of two
   * near-simultaneous clicks, and is generated per call for the same reason as {@link createCart}.
   */
  async startCartFromHubspot(
    payload: StartCartFromHubspotDto,
  ): Promise<StartCartFromHubspotResult> {
    const response = await firstValueFrom(
      this.baseService.http.post(CARTS.START_CART_FROM_HUBSPOT, payload, {
        headers: { 'Idempotency-Key': generateSecureIdempotencyKey() },
      }),
    );
    const data = readObjectFromResponse<StartCartFromHubspotResult>(
      response,
      'cart',
      'reused',
      'deal',
    );
    if (!data?.cart?.id) throw new Error('The cart could not be started.');
    return data;
  }

  /**
   * Start Over (US 21.16): empties the cart and answers with it, reset.
   *
   * The cart is reset **in place** — the same id and cart number come back — so the builder stays
   * where it is and no abandoned draft is left in Saved Carts. Booth holds are released and any
   * gift certificate is returned to its balance, none of which the browser could do for itself.
   *
   * There is no undo, and the endpoint asks for none: the confirmation belongs to the caller, which
   * is why this method does nothing but send. Refused with 400/409 on anything but a draft cart of
   * the admin panel's own making that is not yet attached to an order.
   */
  async startOver(cartId: number | string): Promise<SavedCartDetail> {
    // No `role_id`: Start Over keeps the cart's mode, which only {@link switchContractType} moves.
    const response = await this.baseService.callPostApi(CARTS.START_OVER, { cart_id: cartId });
    const data = readObjectFromResponse<SavedCartDetail>(response, 'id', 'stage');
    if (!data?.id) throw new Error('The cart could not be reset');
    return data;
  }

  /**
   * Today's On-Site drafts for the logged-in user (US 54.24), newest first.
   *
   * Unpaginated by contract — one day of one person's drafts — so there is no page to ask for and
   * an empty array is the "No drafts created today." state, not a failure. `timeZone` is omitted
   * rather than defaulted here: the API's own default is EST, and sending a zone the caller did not
   * choose would quietly decide which day the rep is looking at.
   */
  async listDrafts(timeZone?: CartDraftTimeZone): Promise<CartDraftRow[]> {
    const response = await this.baseService.callGetApi(
      CARTS.DRAFTS,
      timeZone ? { time_zone: timeZone } : {},
    );
    return readListFromResponse<CartDraftRow>(response);
  }

  /**
   * Save as Draft (US 54.25): stamps the cart with its draft identity and answers with it.
   *
   * Sends no body — the cart's contents are already persisted by auto-save, and this only records
   * that the rep parked it. Idempotent, so a second press returns the number already on screen.
   * Refused with 409 on anything but an Onsite cart in Sales mode that is still a draft.
   */
  async saveAsDraft(cartId: number | string): Promise<CartDraftStamp> {
    const response = await this.baseService.callPostApi(CARTS.SAVE_AS_DRAFT(cartId));
    const data = readObjectFromResponse<CartDraftStamp>(response, 'draft_number', 'drafted_at');
    if (!data?.draft_number) throw new Error('The draft could not be saved');
    return data;
  }

  /** Fetch a single saved cart's full detail (unwrapped from the `data` envelope). */
  async getCart(id: number | string): Promise<SavedCartDetail> {
    const response = await this.baseService.callGetApi(CARTS.GET_ONE(id));
    const data = readObjectFromResponse<SavedCartDetail>(response);
    if (!data) throw new Error('Cart not found');
    return data;
  }

  /** Generate the shareable customer portal / cart-review link (returned under `data`). */
  async copyCartLink(id: number | string): Promise<CartCopyLinkResponse> {
    const response = await this.baseService.callPostApi(CARTS.COPY_LINK(id));
    return readObjectFromResponse<CartCopyLinkResponse>(response, 'link') ?? {};
  }

  /** Resend the cart email notification to the customer's original contact email. */
  async resendCart(id: number | string): Promise<CartActionResponse> {
    return (await this.baseService.callPostApi(CARTS.RESEND(id))) as CartActionResponse;
  }

  /** Start an impersonation session for the cart's customer portal. */
  async impersonate(id: number | string): Promise<CartImpersonateResponse> {
    return (await this.baseService.callPostApi(CARTS.IMPERSONATE(id))) as CartImpersonateResponse;
  }

  /** Send a reset-password email to the customer's portal account. */
  async sendResetPassword(id: number | string): Promise<CartActionResponse> {
    return (await this.baseService.callPostApi(
      CARTS.SEND_RESET_PASSWORD(id),
    )) as CartActionResponse;
  }

  /** Invite an additional portal user by email (mirrors the exhibitor additional-user flow). */
  async inviteUser(id: number | string, email: string): Promise<CartInviteUserResponse> {
    return (await this.baseService.callPostApi(CARTS.INVITE_USER(id), {
      email,
    })) as CartInviteUserResponse;
  }

  /** Mark the cart as void (inactive, no longer signable). */
  async voidCart(id: number | string): Promise<CartActionResponse> {
    return (await this.baseService.callPostApi(CARTS.VOID(id))) as CartActionResponse;
  }

  /** Reactivate a voided/expired cart back to an active status. */
  async reactivateCart(id: number | string): Promise<CartActionResponse> {
    return (await this.baseService.callPostApi(CARTS.REACTIVATE(id))) as CartActionResponse;
  }

  /** Duplicate: clones the cart into a new draft and names it. */
  async cloneCart(id: number | string): Promise<CartCloneResult> {
    const response = await this.baseService.callPostApi(CARTS.CLONE(id));
    const data = readObjectFromResponse<CartCloneResult>(response, 'cart_id');
    if (!data?.cart_id) throw new Error('Cart could not be duplicated');
    return data;
  }

  /** Reproduces a placed order as a new, editable draft without changing the order. */
  async cloneOrder(id: number | string): Promise<CartCloneResult> {
    const response = await this.baseService.callPostApi(CARTS.CLONE_ORDER(id));
    const data = readObjectFromResponse<CartCloneResult>(response, 'cart_id');
    if (!data?.cart_id) throw new Error('Order could not be cloned');
    return data;
  }

  /** Deletes the cart for good (soft delete server-side). A signed cart is refused — void it. */
  async deleteCart(id: number | string): Promise<void> {
    await this.baseService.callDeleteApi(CARTS.QUICK_ACTION(id));
  }

  /** The cart customer's additional portal users; the primary account holder is not listed. */
  async listPortalUsers(id: number | string): Promise<CartPortalUser[]> {
    const response = await this.baseService.callGetApi(CARTS.PORTAL_USERS(id));
    return readListFromResponse<CartPortalUser>(response);
  }

  /**
   * Persist edits to a cart's header — stage, notes, client, expiration, Deal Owner. PATCH
   * /carts/:id. A shared cart is edited like any other: the customer link renders the live cart,
   * so there is no confirmation step and no "cart updated" email.
   *
   * @param expectedUpdatedAt The `updated_at` last read. When sent, the save is refused with a 409
   *   if anyone changed the cart in the meantime, instead of silently overwriting their edit.
   */
  async updateCart(
    id: number | string,
    payload: Record<string, unknown>,
    { expectedUpdatedAt }: { expectedUpdatedAt?: string } = {},
  ): Promise<CartActionResponse> {
    const body = expectedUpdatedAt
      ? { ...payload, expected_updated_at: expectedUpdatedAt }
      : payload;
    return (await this.baseService.callPatchApi(CARTS.GET_ONE(id), body)) as CartActionResponse;
  }

  /**
   * Remove one line from the cart and get the recomputed cart back. Generic plumbing rather than
   * Step 2's own: every step that puts a line on the cart takes it off through here.
   *
   * @param force Removes a booth that has dependent lines hanging off it. The route takes nothing
   *   else: with it, a 409 means the cart can no longer be edited or the line belongs to a booth
   *   upgrade — nothing a confirmation gets past (SBE-2829).
   */
  async removeItem(
    id: number | string,
    itemId: string,
    { force = true }: { force?: boolean } = {},
  ): Promise<SavedCartDetail | null> {
    const response = await this.baseService.callDeleteApi(CARTS.CART_ITEM(id, itemId), {
      force,
    });
    return readObjectFromResponse<SavedCartDetail>(response, 'id', 'shows');
  }

  /**
   * Clear All (US 20.21) — every line off the cart in one call, against the emptied cart's own
   * recomputed pricing. Idempotent, so clearing an already-empty cart is a plain 200; the contact,
   * agreement details and payment plan all survive it.
   *
   * There is no `confirm` escape hatch here as there is on {@link removeItem}: a 409 means the cart
   * can no longer be edited at all, which no confirmation gets past.
   */
  async clearItems(id: number | string): Promise<SavedCartDetail | null> {
    const response = await this.baseService.callDeleteApi(CARTS.CART_ITEMS(id));
    return readObjectFromResponse<SavedCartDetail>(response, 'id', 'shows');
  }

  /**
   * Quantity edit on one cart line, answering with the whole recomputed cart — a bundled line's
   * total is not `unit_price × quantity`, so the new figures have to come back from the server.
   */
  async updateItem(
    id: number | string,
    itemId: string,
    payload: UpdateCartItemPayload,
  ): Promise<SavedCartDetail | null> {
    const response = await this.baseService.callPatchApi(CARTS.CART_ITEM(id, itemId), {
      ...payload,
    });
    return readObjectFromResponse<SavedCartDetail>(response, 'id', 'shows');
  }

  /**
   * Pins the booth setup and/or cleaning fee for this cart, answering with the recomputed cart.
   *
   * `body` is sent **as given**. Its keys carry meaning by their presence: an absent fee is left
   * untouched, and `null` clears an override. Normalising or re-spreading it here — anything that
   * could introduce an `undefined` or drop a `null` — would turn "leave alone" and "clear" into
   * each other. See {@link UpdateCartFeesDto}.
   */
  async updateBoothFees(
    id: number | string,
    body: UpdateCartFeesDto,
  ): Promise<SavedCartDetail | null> {
    // Cast, not spread: the same object goes out, so a key the caller left off stays off.
    const response = await this.baseService.callPatchApi(
      CARTS.CART_BOOTH_FEES(id),
      body as unknown as Record<string, unknown>,
    );
    return readObjectFromResponse<SavedCartDetail>(response, 'id', 'shows');
  }

  /**
   * Waives or restores the onsite service charge (SBE-2830), answering with the repriced cart.
   * Restoring uses the rate the cart started with, not today's setting.
   */
  async setOnsiteServiceChargeWaived(
    id: number | string,
    waived: boolean,
  ): Promise<SavedCartDetail | null> {
    const response = await this.baseService.callPatchApi(CARTS.ONSITE_SERVICE_CHARGE(id), {
      waived,
    });
    return readObjectFromResponse<SavedCartDetail>(response, 'id', 'shows');
  }

  /**
   * Send Email (US 21.14): mails the customer the agreement link with the cover email Step 5 saved,
   * moving Draft → Shared - Not Signed Yet (spec 18.13).
   *
   * @param action Which document the Send Email menu asked for. `email_contract_only` on a proposal
   *   cart also moves its stage to contract — the same one-way conversion `POST /carts/:id/convert`
   *   performs — so the caller confirms that with the rep first. Omitted, the cart is sent at the
   *   stage it already has.
   */
  async shareCart(id: number | string, action?: CartShareAction): Promise<CartActionResponse> {
    // multipart because that is the shape the endpoint documents for this call, attachments and all;
    // the files themselves are already on the cart from Step 5, so only the choice is sent here.
    const formData = new FormData();
    if (action) formData.append('action', action);
    return (await this.baseService.callPostApi(
      CARTS.SHARE(id),
      formData as unknown as Record<string, unknown>,
    )) as CartActionResponse;
  }

  /**
   * Signature bypass (US 21.22): the customer signed offline, so the workflow is completed here
   * and the order created — exactly once, with pending status — without waiting on an e-signature.
   *
   * @param file The signed/scanned copy, **optional**. The endpoint validates and attaches it as
   *   the official agreement only when one is sent, so a CS user holding the paper but no scan can
   *   still place the order. The part is omitted entirely rather than sent empty, which would be
   *   validated as a file and rejected.
   */
  async markSigned(id: number | string, file?: File | null): Promise<CartMarkSignedResponse> {
    const formData = new FormData();
    if (file) formData.append('signed_agreement', file);
    return (await this.baseService.callPostApi(
      CARTS.MARK_SIGNED(id),
      formData as unknown as Record<string, unknown>,
    )) as CartMarkSignedResponse;
  }

  /** Download the agreement document (Word or PDF) generated for the cart. */
  async downloadAgreement(
    id: number | string,
    format: AgreementFormat,
  ): Promise<{ blob: Blob; filename: string }> {
    const mime = AGREEMENT_MIME[format];
    const url = format === 'pdf' ? CARTS.AGREEMENT_PDF(id) : CARTS.AGREEMENT_DOCX(id);
    const response = await firstValueFrom(
      this.baseService.http.get(url, {
        observe: 'response',
        responseType: 'blob',
        headers: { Accept: mime },
      }),
    );
    const blob = response.body ?? new Blob([], { type: mime });
    const filename = filenameFromContentDisposition(
      response.headers.get('Content-Disposition'),
      `Agreement-${id}.${format}`,
    );
    return { blob, filename };
  }
}
